/**
 * Main KATA Board v2 Layout Component
 * Container for all 8 components with navigation and state management
 */

import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  ChevronLeft as ChevronLeftIcon, 
  ChevronRight as ChevronRightIcon, 
  CheckCircle as CheckCircleIcon,
  AlertCircle as ExclamationCircleIcon,
  Save as SaveIcon,
  RefreshCw as RefreshIcon,
  Eye as EyeIcon
} from 'lucide-react';

import { useKataBoardV2Store } from '../../stores/kataBoardV2Store';
import { navigationUtils } from '../../utils/kataBoardV2Utils';
import LoadingSpinner from '../common/LoadingSpinner';
import KATABoardV2DisplayMode from './KATABoardV2DisplayMode';
import UnsavedChangesProvider from '../common/UnsavedChangesProvider';
import { KataBoardAccessProvider } from '../../contexts/KataBoardAccessContext';

// Component imports (will be created in next steps)
import Component01_GenericInfo from './components/Component01_GenericInfo';
import Component02_03_CurrentTargetCondition from './components/Component02_03_CurrentTargetCondition';
import Component04_ProcessMap from './components/Component04_ProcessMap';
import Component05_ProcessObservations from './components/Component05_ProcessObservations';
import Component06_PDCACycles from './components/Component06_PDCACycles';
import Component07_IndicatorTrend from './components/Component07_IndicatorTrend';
import Component08_09_BeforeAfterStatus from './components/Component08_09_BeforeAfterStatus';
import Component10_Benefit from './components/Component10_Benefit';

const KATABoardV2Layout: React.FC = () => {
  const { workshopId } = useParams<{ workshopId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const {
    currentKataBoard,
    loading,
    error,
    componentState,
    fetchKataBoardByWorkshop,
    setCurrentComponent,
    setEditing,
    clearError
  } = useKataBoardV2Store();

  const currentComponent = parseInt(searchParams.get('component') || '1');
  const viewMode = searchParams.get('mode') || 'edit'; // 'display' or 'edit'
  const [autoSaving, setAutoSaving] = useState(false);

  useEffect(() => {
    if (workshopId) {
      fetchKataBoardByWorkshop(parseInt(workshopId));
    }
  }, [workshopId, fetchKataBoardByWorkshop]);

  useEffect(() => {
    // Redirect component 9 to component 8 (since we now have 8 components)
    if (currentComponent > 8) {
      setSearchParams({ component: '8' });
      return;
    }
    setCurrentComponent(currentComponent);
  }, [currentComponent, setCurrentComponent, setSearchParams]);

  const handleComponentChange = (componentNumber: number) => {
    if (componentState.hasUnsavedChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to navigate away?')) {
        return;
      }
    }
    
    setSearchParams({ component: componentNumber.toString() });
  };

  const handlePrevious = () => {
    if (currentComponent > 1) {
      handleComponentChange(currentComponent - 1);
    }
  };

  const handleNext = () => {
    if (currentComponent < 8) {
      handleComponentChange(currentComponent + 1);
    }
  };

  const handleSave = async () => {
    setAutoSaving(true);
    // Auto-save logic will be implemented in individual components
    setTimeout(() => setAutoSaving(false), 1000);
  };

  const handleSwitchToDisplayMode = () => {
    setSearchParams({ mode: 'display' });
  };

  const handleSwitchToEditMode = () => {
    setSearchParams({ mode: 'edit', component: currentComponent.toString() });
  };

  const handleBackFromDisplay = () => {
    // Go back to workshops list or previous page
    window.history.back();
  };

  const renderCurrentComponent = () => {
    if (!currentKataBoard) return null;

    switch (currentComponent) {
      case 1: return <Component01_GenericInfo kataBoard={currentKataBoard} />;
      case 2: return <Component02_03_CurrentTargetCondition kataBoard={currentKataBoard} />;
      case 3: return <Component04_ProcessMap kataBoard={currentKataBoard} />;
      case 4: return <Component05_ProcessObservations kataBoard={currentKataBoard} />;
      case 5: return <Component06_PDCACycles kataBoard={currentKataBoard} />;
      case 6: return <Component07_IndicatorTrend kataBoard={currentKataBoard} />;
      case 7: return <Component08_09_BeforeAfterStatus kataBoard={currentKataBoard} />;
      case 8: return <Component10_Benefit kataBoard={currentKataBoard} />;
      default: return <div>Component not found</div>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error Loading KATA Board</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={clearError}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <RefreshIcon className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!currentKataBoard) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">KATA Board Not Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No KATA Board found for workshop {workshopId}
          </p>
        </div>
      </div>
    );
  }

  // Additional safety check for workshop_info
  if (!currentKataBoard.workshop_info) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900">Loading Workshop Details</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please wait while we fetch the workshop information...
          </p>
        </div>
      </div>
    );
  }

  const completionPercentage = currentKataBoard ? navigationUtils.getCompletionPercentage(currentKataBoard) : 0;
  const canEdit = currentKataBoard.workshop_info?.can_edit === true;

  // Render Display Mode
  if (viewMode === 'display') {
    return (
      <KataBoardAccessProvider canEdit={canEdit}>
        <KATABoardV2DisplayMode
          kataBoard={currentKataBoard}
          onSwitchToEditMode={canEdit ? handleSwitchToEditMode : undefined}
          onBack={handleBackFromDisplay}
        />
      </KataBoardAccessProvider>
    );
  }

  // View-only users keep tab navigation; mutation controls are hidden in each tab.
  return (
    <KataBoardAccessProvider canEdit={canEdit}>
    <UnsavedChangesProvider 
      hasUnsavedChanges={canEdit && componentState.hasUnsavedChanges}
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to navigate away?"
    >
      <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Workshop Info */}
            <div className="flex items-center">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  KATA Board - {currentKataBoard.workshop_info?.workshop_number || 'Loading...'}
                </h1>
                <p className="text-sm text-gray-500">
                  {currentKataBoard.workshop_info?.title || 'Loading workshop details...'}
                </p>
              </div>
            </div>

            {/* Progress and Actions */}
            <div className="flex items-center space-x-4">
              {/* Progress */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Progress:</span>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {completionPercentage}%
                </span>
              </div>

              {!canEdit && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  View only
                </span>
              )}

              {/* Mode Toggle */}
              <button
                onClick={handleSwitchToDisplayMode}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                title="Switch to Display Mode"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Display Mode
              </button>

              {/* Auto Save */}
              {canEdit && componentState.hasUnsavedChanges && (
                <button
                  onClick={handleSave}
                  disabled={autoSaving}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {autoSaving ? (
                    <RefreshIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <SaveIcon className="h-4 w-4 mr-2" />
                  )}
                  {autoSaving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {Array.from({ length: 8 }, (_, i) => i + 1).map((componentNumber) => {
              const isActive = componentNumber === currentComponent;
              const isComplete = currentKataBoard ? navigationUtils.isComponentComplete(currentKataBoard, componentNumber) : false;
              const componentName = navigationUtils.getComponentName(componentNumber);

              return (
                <button
                  key={componentNumber}
                  onClick={() => handleComponentChange(componentNumber)}
                  className={`
                    flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap
                    ${isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <span className={`
                    flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                    ${isActive
                      ? 'bg-blue-500 text-white'
                      : isComplete
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                    }
                  `}>
                    {isComplete && !isActive ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      componentNumber
                    )}
                  </span>
                  <span className="hidden md:block">{componentName}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm">
          {/* Component Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  {currentComponent}. {navigationUtils.getComponentName(currentComponent)}
                </h2>
                {canEdit && componentState.isEditing && (
                  <p className="mt-1 text-sm text-blue-600">
                    Editing mode - Changes will be auto-saved
                  </p>
                )}
                {!canEdit && (
                  <p className="mt-1 text-sm text-amber-700">
                    Read-only access for this plant
                  </p>
                )}
              </div>
              
              {/* Navigation Buttons */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevious}
                  disabled={currentComponent === 1}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                
                <button
                  onClick={handleNext}
                  disabled={currentComponent === 8}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>

          {/* Component Content */}
          <div className="p-6">
            {renderCurrentComponent()}
          </div>
        </div>
      </div>

      {/* Floating Progress Indicator */}
      <div className="fixed bottom-6 right-6">
        <div className="bg-white rounded-full shadow-lg p-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-xs font-bold text-blue-600">
                {currentComponent}
              </span>
            </div>
            <div className="text-sm">
              <div className="font-medium text-gray-900">Component {currentComponent}</div>
              <div className="text-gray-500">of 8</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </UnsavedChangesProvider>
    </KataBoardAccessProvider>
  );
};

export default KATABoardV2Layout;
